/**
 * Шаринг — ОДИН модуль на весь клиент.
 *
 * Почему отдельно, а не inline в каждом компоненте: мест использования два
 * (MediaViewer шарит файл, контекстное меню сообщения шарит текст+ссылку),
 * а путей выполнения ТРИ: нативный мост оболочки (Android/Capacitor,
 * см. nativeShare.ts), системный Web Share API браузера и запасной путь
 * (скачивание/копирование — у вызывающего). Держать копии этой маршрутизации
 * в компонентах — ровно то дублирование, из-за которого однажды одна кнопка
 * станет обманкой, а вторая — нет (Антон, патч 2026-09-10 10:24: кнопку-
 * обманку не рисуем).
 *
 * Замер 2026-10-05 (Антон, подтверждено повторно): в Android WebView
 * оболочки `navigator.share` нет — кнопки не рисовались, шаринга на
 * телефоне не было вовсе. Поэтому проверки ниже учитывают И нативный мост.
 *
 * Почему canShare, а не просто наличие navigator.share: desktop Firefox
 * функцию share экспонирует, но на вызов отвечает ошибкой. canShare
 * отвечает честно. Где canShare нет вовсе (старые реализации) — верим
 * наличию share: лучше рискнуть, чем спрятать рабочую кнопку.
 */
import {
  canNativeShareFiles,
  canNativeShareText,
  isShareCanceled,
  nativeShareFile,
  nativeShareText,
} from "./nativeShare";

/** Результат попытки шаринга — вызывающий решает, что показать. */
export type ShareOutcome =
  | "shared" // системное/нативное меню отработало (или пользователь отменил)
  | "canceled" // пользователь сам закрыл меню — не ошибка, не тревога
  | "unavailable"; // ни один путь не сработал → запасной путь у вызывающего

/** Умеет ли устройство шарить ФАЙЛЫ (кнопка «Поделиться» в просмотрщике). */
export function canShareFiles(): boolean {
  if (canNativeShareFiles()) return true;
  try {
    if (typeof navigator.canShare !== "function") return false;
    const probe = new File([new Uint8Array(1)], "probe.bin", {
      type: "application/octet-stream",
    });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/** Умеет ли устройство шарить ТЕКСТ+ССЫЛКУ (пункт «Поделиться» у сообщения). */
export function canShareText(): boolean {
  if (canNativeShareText()) return true;
  try {
    if (typeof navigator.share !== "function") return false;
    if (typeof navigator.canShare !== "function") return true;
    return navigator.canShare({
      text: "probe",
      url: "https://matrix.to/",
    });
  } catch {
    return false;
  }
}

/**
 * Поделиться ТЕКСТОМ/ССЫЛКОЙ: сначала нативный мост оболочки, затем
 * системный Web Share браузера. «unavailable» — обоих нет или оба упали:
 * вызывающий обязан показать запасной путь (копирование ссылки уже лежит
 * в меню рядом отдельным пунктом).
 */
export async function shareText(options: { text: string; title?: string }): Promise<ShareOutcome> {
  if (canNativeShareText()) {
    try {
      await nativeShareText({ text: options.text, title: options.title });
      return "shared";
    } catch (e) {
      if (isShareCanceled(e)) return "canceled";
      // Мост объявлен, но вызов упал — пробуем Web Share ниже.
    }
  }
  try {
    if (typeof navigator.share !== "function") return "unavailable";
    await navigator.share({ text: options.text, title: options.title });
    return "shared";
  } catch (e) {
    // AbortError = пользователь сам закрыл системное меню — это не ошибка,
    // показывать её как сбой было бы ложной тревогой.
    if ((e as Error).name === "AbortError") return "canceled";
    console.error("share failed", e);
    return "unavailable";
  }
}

/**
 * Поделиться ФАЙЛОМ: нативный мост (Blob → временный файл → Intent), затем
 * Web Share Level 2. «unavailable» — вызывающий обязан уйти в запасной
 * путь (скачивание) С ПОНЯТНЫМ сообщением, а не молчать.
 */
export async function shareFile(blob: Blob, name: string, mime: string): Promise<ShareOutcome> {
  if (canNativeShareFiles()) {
    try {
      await nativeShareFile(blob, name, mime);
      return "shared";
    } catch (e) {
      if (isShareCanceled(e)) return "canceled";
    }
  }
  try {
    if (typeof navigator.share !== "function") return "unavailable";
    const file = new File([blob], name, {
      type: mime || blob.type || "application/octet-stream",
    });
    await navigator.share({ files: [file], title: name });
    return "shared";
  } catch (e) {
    if ((e as Error).name === "AbortError") return "canceled";
    console.error("share failed", e);
    return "unavailable";
  }
}
